import Select from '@xver/components/Atoms/Select/Select';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { ProjectModel } from '@xver/apis/v1/projects/model';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import {
    invalidateProjectQueries,
    useProjectFeedback,
    useProjectMembersQuery,
    useProjectResourcesQuery,
} from '@xver/hooks/useProjectQueries';
import { getErrorCode } from '@xver/utils/error';
import { formatBaht } from '@xver/utils/number';
import {
    billingEstimate,
    focusAfterRender,
    memberOptionLabel,
    SELECT_SEARCHABLE_AFTER,
} from '@xver/utils/projects';
import { FormEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BillingTabPanelProps {
    project: ProjectModel;
    viewerId: number | undefined;
}

type PayerAction =
    | { kind: 'set'; userId: number | null }
    | { kind: 'accept' }
    | { kind: 'decline' };

export default function BillingTabPanel({ project, viewerId }: BillingTabPanelProps) {
    const { t } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { errorMessage, notifySuccess, notifyError } = useProjectFeedback();
    const isOwner = project.role === 'owner';
    const { payer } = project;
    const isNominee = payer.status === 'pending' && payer.userId === viewerId;
    const isPayer = payer.status === 'accepted' && payer.userId === viewerId && !isOwner;
    const members = useProjectMembersQuery(project.id);
    const resources = useProjectResourcesQuery(project.id);
    const [choice, setChoice] = useState('');
    const [choiceError, setChoiceError] = useState<string>();
    const [resignOpen, setResignOpen] = useState(false);
    const [removeOpen, setRemoveOpen] = useState(false);
    const selectRef = useRef<HTMLInputElement>(null);
    const resignButtonId = useId();
    const statusId = useId();

    const action = useMutation({
        mutationFn: (payerAction: PayerAction): Promise<unknown> => {
            if (payerAction.kind === 'set') {
                return Endpoints.v1.Projects.setPayer(project.id, {
                    userId: payerAction.userId,
                    ...(payerAction.userId === null && payer.status
                        ? { expectStatus: payer.status }
                        : {}),
                });
            }
            return payerAction.kind === 'accept'
                ? Endpoints.v1.Projects.acceptPayer(project.id)
                : Endpoints.v1.Projects.declinePayer(project.id);
        },
        onSuccess: async (_, payerAction) => {
            setResignOpen(false);
            setRemoveOpen(false);
            setChoice('');
            const key =
                payerAction.kind === 'set'
                    ? payerAction.userId === null
                        ? payer.status === 'pending'
                            ? 'cancelled'
                            : 'removed'
                        : 'nominated'
                    : payerAction.kind === 'accept'
                      ? 'accepted'
                      : isPayer
                        ? 'resigned'
                        : 'declined';
            notifySuccess(
                t(`billing.toast.${key}`),
                t('billing.toast.message', { name: project.name })
            );
            await invalidateProjectQueries(queryClient);
            focusAfterRender(
                () =>
                    (payerAction.kind === 'accept' && document.getElementById(resignButtonId)) ||
                    document.getElementById(statusId)
            );
        },
        onError: async (error) => {
            if (getErrorCode(error) !== 'payer_changed') {
                notifyError(error);
                return;
            }
            setRemoveOpen(false);
            notifications.show({
                title: t('billing.toast.changed'),
                message: errorMessage(error),
                icon: <IconInfoCircle size={18} aria-hidden="true" />,
            });
            await invalidateProjectQueries(queryClient);
            focusAfterRender(() => document.getElementById(statusId));
        },
    });

    const nonOwners = (members.data ?? []).filter((member) => member.role !== 'owner');
    const candidates = nonOwners.filter((member) => member.userId !== payer.userId);
    const payerName = payer.name || t('billing.someone');
    const estimate = billingEstimate(resources.data);
    const pendingKind = action.isPending ? action.variables?.kind : undefined;
    const nominating =
        action.isPending && action.variables?.kind === 'set' && action.variables.userId !== null;
    const clearing =
        action.isPending && action.variables?.kind === 'set' && action.variables.userId === null;
    const limitedEstimate =
        !!resources.data && !('instances' in resources.data && 'baremetals' in resources.data);

    const handleNominate = (event: FormEvent) => {
        event.preventDefault();
        if (action.isPending) return;
        if (!choice) {
            setChoiceError(t('billing.errors.chooseMember'));
            selectRef.current?.focus();
            return;
        }
        setChoiceError(undefined);
        action.mutate({ kind: 'set', userId: Number(choice) });
    };

    return (
        <div className="w-full flex flex-col gap-6">
            <DialogBox label={t('billing.payerTitle')} description={t('billing.payerDescription')}>
                <div className="w-full p-4 flex flex-col gap-4 text-sm">
                    <p id={statusId} tabIndex={-1} className="max-w-[80ch]">
                        {payer.status === ''
                            ? t('billing.status.none', { owner: project.owner.name })
                            : payer.status === 'pending'
                              ? t('billing.status.pending', {
                                    name: payerName,
                                    owner: project.owner.name,
                                })
                              : t('billing.status.accepted', { name: payerName })}
                    </p>

                    {isNominee && (
                        <div className="flex flex-col gap-3 p-3 rounded-md border-[0.5px] border-line bg-surface-2">
                            <p className="font-bold">
                                {t('billing.nominee.title', { owner: project.owner.name })}
                            </p>
                            <p className="max-w-[80ch] text-fg/78">
                                {t('requests.payer.consequence')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    size="lg"
                                    onClick={() => action.mutate({ kind: 'accept' })}
                                    isLoading={pendingKind === 'accept'}
                                    disabled={action.isPending && pendingKind !== 'accept'}
                                >
                                    {t('requests.payer.accept')}
                                </Button>
                                <Button
                                    size="lg"
                                    variant="secondary"
                                    onClick={() => action.mutate({ kind: 'decline' })}
                                    isLoading={pendingKind === 'decline'}
                                    disabled={action.isPending && pendingKind !== 'decline'}
                                >
                                    {t('requests.decline')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {isPayer && (
                        <div>
                            <Button
                                id={resignButtonId}
                                size="lg"
                                variant="secondary"
                                onClick={() => setResignOpen(true)}
                            >
                                {t('billing.resign.open')}
                            </Button>
                        </div>
                    )}

                    {isOwner && (
                        <form
                            onSubmit={handleNominate}
                            noValidate
                            className="w-full max-w-xl flex flex-col gap-3"
                        >
                            {members.isError ? (
                                <p role="alert">{errorMessage(members.error)}</p>
                            ) : candidates.length === 0 && !members.isPending ? (
                                nonOwners.length === 0 && (
                                    <p className="text-fg/78">{t('billing.noCandidates')}</p>
                                )
                            ) : (
                                <>
                                    <Select
                                        ref={selectRef}
                                        size="md"
                                        label={
                                            payer.status === ''
                                                ? t('billing.chooseLabel')
                                                : t('billing.changeLabel')
                                        }
                                        description={t('billing.chooseDescription')}
                                        placeholder={t('billing.choosePlaceholder')}
                                        data={candidates.map((member) => ({
                                            value: String(member.userId),
                                            label: memberOptionLabel(member),
                                        }))}
                                        value={choice || null}
                                        onChange={(value) => {
                                            setChoice(value ?? '');
                                            setChoiceError(undefined);
                                        }}
                                        allowDeselect={false}
                                        searchable={candidates.length > SELECT_SEARCHABLE_AFTER}
                                        nothingFoundMessage={t('switcher.nothingFound')}
                                        disabled={members.isPending}
                                        error={choiceError}
                                    />
                                    <div>
                                        <Button size="lg" type="submit" isLoading={nominating}>
                                            {t('billing.nominate')}
                                        </Button>
                                    </div>
                                </>
                            )}
                            {payer.status !== '' && (
                                <div>
                                    <Button
                                        size="lg"
                                        variant="secondary"
                                        onClick={() =>
                                            payer.status === 'pending'
                                                ? action.mutate({ kind: 'set', userId: null })
                                                : setRemoveOpen(true)
                                        }
                                        isLoading={clearing}
                                        disabled={action.isPending && !clearing}
                                    >
                                        {payer.status === 'pending'
                                            ? t('billing.cancelNomination')
                                            : t('billing.removePayer')}
                                    </Button>
                                </div>
                            )}
                        </form>
                    )}
                </div>
            </DialogBox>

            <DialogBox label={t('billing.rulesTitle')}>
                <ul className="w-full p-4 ps-9 list-disc flex flex-col gap-2 text-sm max-w-[80ch]">
                    <li>{t('billing.rules.payerFirst')}</li>
                    <li>{t('billing.rules.fallback')}</li>
                    <li>{t('billing.rules.dunning')}</li>
                    <li>{t('billing.rules.baremetalLaunch')}</li>
                    <li>{t('billing.rules.memberSeat')}</li>
                </ul>
            </DialogBox>

            <DialogBox
                label={t('billing.estimateTitle')}
                description={t('billing.estimateDescription')}
            >
                <div className="w-full p-4 flex flex-col gap-2 text-sm">
                    {resources.isPending ? (
                        <p role="status">{t('resources.loading')}</p>
                    ) : resources.isError ? (
                        <p role="alert">{errorMessage(resources.error)}</p>
                    ) : (
                        <ul className="flex flex-col gap-2">
                            {resources.data?.instances && (
                                <li>
                                    {t('billing.estimate.instances', {
                                        count: estimate.instanceCount,
                                        price: formatBaht(estimate.hourly),
                                    })}
                                </li>
                            )}
                            {estimate.baremetal.map((entry) => (
                                <li key={entry.period}>
                                    {t('billing.estimate.baremetal', {
                                        count: entry.count,
                                        price: formatBaht(entry.total),
                                        period: t(`billing.periods.${entry.period}`),
                                    })}
                                </li>
                            ))}
                            <li className="text-fg/78">{t('billing.estimate.containers')}</li>
                            {limitedEstimate && (
                                <li className="text-fg/78">{t('billing.estimate.limited')}</li>
                            )}
                        </ul>
                    )}
                </div>
            </DialogBox>

            <ConfirmActionModal
                opened={resignOpen}
                onClose={() => {
                    if (!action.isPending) setResignOpen(false);
                }}
                title={t('billing.resign.title', { name: project.name })}
                message={t('billing.resign.message', { owner: project.owner.name })}
                confirmLabel={t('billing.resign.confirm')}
                confirmVariant="danger"
                isLoading={action.isPending}
                onConfirm={() => action.mutate({ kind: 'decline' })}
            />

            <ConfirmActionModal
                opened={removeOpen}
                onClose={() => {
                    if (!action.isPending) setRemoveOpen(false);
                }}
                title={t('billing.remove.title', { name: payerName })}
                message={t('billing.remove.message', { owner: project.owner.name })}
                confirmLabel={t('billing.remove.confirm')}
                confirmVariant="danger"
                isLoading={action.isPending}
                onConfirm={() => action.mutate({ kind: 'set', userId: null })}
            />
        </div>
    );
}
